import {InputText} from '@/components/commons/input-text/InputText'
import {Button} from '@/components/ui/button/Button'
import {Divider} from '@/components/ui/divider/Divider'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {PlusIcon, Trash01Icon} from '@/components/ui/icon/Icon'
import {Text} from '@/components/ui/text/Text'
import {InputSelectAccessType} from '@/features/access-types/components/input-select-access-type/InputSelectAccessType'
import {AccessType} from '@/features/access-types/types'
import {PlannedAccessTeamMeetingFields} from '@/features/planned-accesses/components/accesses-form/planned-access-team-meeting-fields/PlannedAccessTeamMeetingFields'
import {PlannedAccessesFieldsSchema} from '@/features/planned-accesses/formSchemas'
import {ProjectExtended} from '@/features/projects/types'
import {useTranslation} from '@/translations/i18n'
import {useMemo} from 'react'
import {Controller, useFieldArray, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const PlannedAccessesAbsenceFieldsSchema = PlannedAccessesFieldsSchema.pick({
    absenceAccesses: true
})
type PlannedAccessesAbsenceFieldsSchema = z.infer<typeof PlannedAccessesAbsenceFieldsSchema>

export const PlannedAccessesAbsenceFields = ({project}: {project: ProjectExtended | undefined}) => {
    const {t} = useTranslation()
    const {control, formState} = useFormContext<PlannedAccessesAbsenceFieldsSchema>()
    const watchAbsenceAccesses = useWatch({control, name: 'absenceAccesses'})

    const {
        fields: absenceAccesses,
        append,
        remove
    } = useFieldArray({
        control,
        name: 'absenceAccesses',
        keyName: 'fieldId'
    })

    const onAddAbsenceAccess = () => {
        append({
            accessType: undefined,
            numberOfAccesses: undefined
        })
    }

    const disabledAccessTypes = useMemo((): AccessType['accessType'][] => {
        return watchAbsenceAccesses
            .filter(field => !!field?.accessType?.accessType)
            .map(field => field.accessType!.accessType) // Add non-null assertion since we filtered out nulls
    }, [watchAbsenceAccesses])

    return (
        <>
            {!absenceAccesses || absenceAccesses.length == 0 ? (
                <Text palette="danger" shade="700" size="textSm">
                    {t('errors:default')}
                </Text>
            ) : (
                absenceAccesses.map((field, index) => (
                    <Flexbox key={field.fieldId} fullWidth gap={2} align="flex-end">
                        <Grid columns={3} fullWidth gap={2.5}>
                            <GridItem>
                                <Controller
                                    control={control}
                                    name={`absenceAccesses.${index}.accessType`}
                                    render={({field}) => (
                                        <InputSelectAccessType
                                            label={t('commons:access_type')}
                                            placeholder={t('commons:select')}
                                            onChange={field.onChange}
                                            value={field.value}
                                            groupName="non_in_person_accesses"
                                            disabledAccessTypes={disabledAccessTypes}
                                            errorMessage={
                                                formState.errors?.absenceAccesses?.[index]?.accessType?.message
                                            }
                                        />
                                    )}
                                />
                            </GridItem>
                            <GridItem justify="space-between" gap={2}>
                                <Controller
                                    control={control}
                                    name={`absenceAccesses.${index}.numberOfAccesses`}
                                    render={({field}) => (
                                        <>
                                            {watchAbsenceAccesses[index]?.accessType?.accessType == 'team_meeting' ||
                                            watchAbsenceAccesses[index]?.accessType?.accessType == 'glo_meeting' ? (
                                                <PlannedAccessTeamMeetingFields
                                                    value={field.value || 0}
                                                    onChange={field.onChange}
                                                    teamMembersLength={
                                                        project?.team.filter(teamMember => !teamMember.isAReserve)
                                                            .length || 0
                                                    }
                                                />
                                            ) : (
                                                <InputText
                                                    label={t('commons:number')}
                                                    type="number"
                                                    value={field.value ?? ''}
                                                    min={0}
                                                    onChange={event =>
                                                        field.onChange(
                                                            event.target.value ? parseInt(event.target.value) : ''
                                                        )
                                                    }
                                                    placeholder="-"
                                                    errorMessage={
                                                        formState.errors?.absenceAccesses?.[index]?.numberOfAccesses
                                                            ?.message
                                                    }
                                                />
                                            )}
                                        </>
                                    )}
                                />
                            </GridItem>
                            <GridItem align="flex-end" gap={2.5}>
                                <Button
                                    variant="ghost"
                                    onClick={() => remove(index)}
                                    shape="square"
                                    disabled={absenceAccesses.length == 1}
                                >
                                    <Trash01Icon />
                                </Button>
                            </GridItem>
                        </Grid>
                    </Flexbox>
                ))
            )}
            <Divider direction="horizontal" />
            <Button variant="secondary" size="sm" onClick={onAddAbsenceAccess}>
                <PlusIcon />
                {t('planned_accesses:absence_access')}
            </Button>
        </>
    )
}
